/**
 * MER-614: the bucket size a tile's number is grouped at, on the tile.
 *
 * Zay, 2026-09-02: "I know that when you go to weekly you still see the last
 * 30 days on disposal, at least." Selecting Weekly does not add a 30-day
 * window, it removes a rewrite: the Overview disposal tiles are authored
 * `range: last_30_days`, monthly grain rewrites that to `last_month`
 * (grainDefaults.mjs) and weekly leaves it alone. Two different numbers, and
 * the only thing on the tile that moved was a range label neither reader had
 * a reason to connect to the Period selector.
 *
 * The chip on a tile face carried the effective RANGE (MER-421), the scope
 * notes (MER-504 / MER-512 / MER-694) and the feed's last day (MER-693). It
 * never carried the grain, so a monthly tile and a weekly tile reading the
 * same window were indistinguishable, and two tiles sitting side by side under
 * the same "LAST 30 DAYS" could be summing weekly buckets and counting raw
 * events over two genuinely different windows.
 *
 * What this module does NOT claim: a grain for a tile that has none. Tiles on
 * event-grain sources (service_event, ar_transactions) carry no `period_type`
 * at all -- they count rows inside a date window, and there is no bucket to
 * name. Those get no grain word rather than an invented one.
 *
 * Pure .mjs so node:test imports the real functions with no build step (the
 * grainDefaults.mjs / lobFilter.mjs precedent); tileWindow.d.mts types the TS
 * callers.
 */

/**
 * Effective `filter.period_type` -> the word on the chip.
 *
 * `snapshot` is included deliberately. It is a real bucket answer, and the
 * one an owner most needs: an AR balance is a reading taken on a day, not a
 * total accumulated over the window beside it.
 */
const GRAIN_LABELS = Object.freeze({
  monthly: "Monthly",
  weekly: "Weekly",
  daily: "Daily",
  snapshot: "Snapshot",
});

/**
 * @param {unknown} periodType the widget's EFFECTIVE filter.period_type, i.e.
 *   read AFTER applyGlobalFilters, where the declared grain, an explicit
 *   Period selection and the MER-362 auto-apply have already been resolved.
 * @returns {string | null} the display word, or null when the tile has no
 *   grain to show (no period_type, or a value this module does not know).
 */
export function tileGrainLabel(periodType) {
  if (typeof periodType !== "string") return null;
  return GRAIN_LABELS[periodType] ?? null;
}

/* ------------------------------------------------------------------ */
/* MER-614 defects 2 and 3: the window a tile ACTUALLY ran              */
/* ------------------------------------------------------------------ */

/**
 * MER-614 defect 2, "the label is inaccurate at both grains", and defect 3,
 * "for a single-number rollup, Weekly is a five-week total, not a weekly
 * figure".
 *
 * WHAT IS LEFT OF THOSE TWO. The ticket called bucket-selection-by-overlap
 * the root fix, and that half shipped at MER-636: a trailing window over
 * period-bucketed rows now ends at the last COMPLETE bucket and snaps its
 * start to the nearest boundary, so the five-week total is a four-week total
 * and no longer swings with today's day of week. The SELECTION was corrected
 * and the LABEL was not. On 3 September 2026, the date of the review this
 * ticket came out of, a weekly tile reading `last_30_days` covered 3 to 30
 * August; the chip said "Last 30 days" then and says "Last 30 days" now.
 *
 * Two readings on that one chip are wrong, and they are the ticket's two
 * remaining defects:
 *
 *   - It names a DURATION the window does not have. Four whole weeks is 28
 *     days, not 30, and it ends at the last complete bucket rather than
 *     today. A reader on 22 September who is told "Last 30 days" reasonably
 *     believes the number includes last weekend. It does not: the window
 *     closed on the 20th.
 *   - It says nothing about HOW MANY buckets were added. A single-number
 *     tile under a "Weekly" chip reads as a weekly figure. It is a four-week
 *     total, and no word on the face says so.
 *
 * THE RULE. Name the window the query actually ran, in the buckets it ran it
 * in: "4 weeks to Sep 20". The count answers defect 3 and the closing date
 * answers defect 2, in a phrase shorter than the "Weekly / Last 30 days" it
 * replaces. It is derived from periodWindowBounds, the same function that
 * SELECTS the buckets, so the label and the data cannot drift: there is one
 * rule and the chip reads it back.
 *
 * WHERE IT DOES NOT APPLY, and why each exclusion is a fact and not a taste:
 *
 *   - Ranges that name a CALENDAR PERIOD rather than a duration (this_week,
 *     last_week, this_month, last_month, and the MER-692 `month:YYYY-MM`
 *     token). "Last complete month" is already exactly true, and MER-421
 *     shipped it as the answer to Delta's A3 ask. Only a range that claims a
 *     NUMBER OF DAYS can be caught claiming the wrong one.
 *   - Event-grain sources. Their `last_30_days` really is a bare cutoff at
 *     today minus 30, so the label is accurate and there is no bucket to
 *     count. Naming a bucket window there would be the invented-grain error
 *     tileGrainLabel already refuses.
 *   - Snapshot tiles. applyTimeline never windows them.
 *   - Any case periodWindowBounds has no opinion on, which is the one that
 *     matters most: a non-monthly grain on a TEXT month-key column returns
 *     null and applyTimeline keeps its pre-MER-636 bare cutoff. The label
 *     must describe the window that RAN, so it is gated on bounds for the
 *     widget's OWN column and goes quiet wherever that fallback is taken.
 *
 * THE TWO CALLS. periodWindowBounds serializes to the column's storage type,
 * so a TEXT period column answers in bare `YYYY-MM` keys that cannot be
 * parsed back into a day. The first call, on the widget's real column,
 * decides WHETHER the bucket branch runs at all; the second, on `period_date`,
 * does the arithmetic in ISO dates. Both are the same grain, range and width,
 * so they select the same buckets by construction and only the spelling
 * differs. grainMismatchCopy in periodWindow.mjs probes on `period_date` for
 * the same reason.
 */

import { CURRENT_BUCKET_RANGES, periodWindowBounds } from "./periodWindow.mjs";
import { MONTH_NAMES, parseMonthToken } from "./calendarMonth.mjs";

/**
 * Ranges that name a calendar period rather than a width. Their existing
 * labels state a calendar fact and cannot be caught overclaiming a duration,
 * so they keep them. `this_month` and `last_month` additionally never reach
 * the bucket branch at all: applyTimeline answers them with literal calendar
 * month bounds one branch earlier.
 */
const CALENDAR_NAMED_RANGES = new Set(["this_week", "last_week", "this_month", "last_month"]);

/**
 * Aggregations that genuinely ADD their buckets together, and so are the only
 * ones a reader can be warned are a total.
 *
 * Found on review, and it is this ticket's own error class turned on itself:
 * keying the warning on `kpi_subtype: single_number` told 59 of the 99
 * period-grain single-number tiles on DWS that an average, a maximum, a
 * latest reading or a distinct count was "the total across all 4 of them".
 * Eleven of those render as percentages, so the sentence invited a reader to
 * divide a percentage by four. A number described as something it is not is
 * exactly what MER-614 exists to remove.
 *
 * `latest`, `max`, `min` and `distinct_count` are not sums either, and a
 * DERIVED_RATIOS or WEIGHTED_RATIOS read divides one sum by another, which is
 * a rate over the window rather than a total of it. All of them get the
 * window sentence and none of them gets the total sentence.
 */
const ADDITIVE_AGGREGATIONS = new Set(["sum", "count"]);

/** The word for one bucket at each grain. */
const GRAIN_UNITS = Object.freeze({ monthly: "month", weekly: "week", daily: "day" });

function parseIso(value) {
  if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const y = Number(value.slice(0, 4));
  const m = Number(value.slice(5, 7));
  const d = Number(value.slice(8, 10));
  const out = new Date(y, m - 1, d);
  // Round-trip guard: Date silently normalizes 2026-02-31 into March.
  if (out.getFullYear() !== y || out.getMonth() !== m - 1 || out.getDate() !== d) return null;
  return out;
}

/** Whole buckets in [start, end). Both are boundaries, so this terminates. */
function countBuckets(start, end, grain) {
  if (grain === "monthly") {
    return (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
  }
  const days = Math.round(
    (Date.UTC(end.getFullYear(), end.getMonth(), end.getDate()) -
      Date.UTC(start.getFullYear(), start.getMonth(), start.getDate())) /
      86400000,
  );
  return grain === "weekly" ? Math.round(days / 7) : days;
}

/**
 * "Sep 20", or "Dec 31 2026" when the day is not in the reader's own year.
 *
 * The year is not decoration. On 15 January 2027 a monthly tile reads
 * "1 month to Dec 31", and December of which year is a real question: the
 * window is December 2026, the reader is in 2027, and nothing on the chip
 * says which. Carrying the year always would cost four characters on every
 * tile for a question almost nobody is asking, so it appears exactly when it
 * is load-bearing.
 *
 * The month names come from calendarMonth.mjs so this spelling and the one
 * monthLabel() renders cannot drift apart.
 */
function dayLabel(d, today) {
  const base = `${MONTH_NAMES[d.getMonth()]} ${d.getDate()}`;
  return d.getFullYear() === today.getFullYear() ? base : `${base} ${d.getFullYear()}`;
}

/**
 * The window a period-bucketed tile actually ran, in its own buckets, or null
 * when this module has nothing truer to say than the range label already on
 * the tile.
 *
 * @param {{
 *   grain: string | undefined,
 *   range: string | undefined,
 *   days: number | undefined,
 *   column: string | undefined,
 *   isPeriodGrainSource: boolean,
 *   isSnapshot: boolean,
 *   today: Date,
 * }} input
 *   `grain` is the EFFECTIVE filter.period_type and `range` the EFFECTIVE
 *   timeline.range, both read after applyGlobalFilters and after the demo-mode
 *   rewrite, because those are what applyTimeline ran. `days` is the
 *   TIMELINE_DAY_RANGES width for that range.
 * @returns {{ note: string, buckets: number, unit: string, through: string } | null}
 */
export function resolvedWindow(input = {}) {
  const { grain, range, days, column, isPeriodGrainSource, isSnapshot, today } = input;
  if (!isPeriodGrainSource || isSnapshot === true) return null;
  const unit = GRAIN_UNITS[String(grain)];
  if (!unit) return null;
  if (!range || CALENDAR_NAMED_RANGES.has(range) || CURRENT_BUCKET_RANGES.has(range)) return null;
  // MER-692 month tokens already name themselves ("Weeks of Jul 2026").
  if (parseMonthToken(range)) return null;
  if (!days || days <= 0) return null;
  if (!column) return null;

  // Gate on the widget's OWN column: null here means applyTimeline kept its
  // bare cutoff and no bucket window ran.
  const onColumn = periodWindowBounds({ grain, range, days, column, today });
  if (!onColumn || onColumn.empty) return null;

  const measured = periodWindowBounds({ grain, range, days, column: "period_date", today });
  if (!measured || measured.empty) return null;
  const start = parseIso(measured.gte);
  const end = parseIso(measured.lt);
  if (!start || !end) return null;
  const buckets = countBuckets(start, end, grain);
  if (!Number.isInteger(buckets) || buckets < 1) return null;

  // `lt` is exclusive, so the last day the window includes is the day before.
  const through = new Date(end.getFullYear(), end.getMonth(), end.getDate() - 1);
  return {
    note: `${buckets} ${unit}${buckets === 1 ? "" : "s"} to ${dayLabel(through, today)}`,
    buckets,
    unit,
    through: `${through.getFullYear()}-${String(through.getMonth() + 1).padStart(2, "0")}-${String(through.getDate()).padStart(2, "0")}`,
  };
}

/**
 * The same fact in a sentence, for the tile's info panel, following the
 * MER-553 precedent: the chip carries the short form and the panel carries
 * the mechanism in plain words.
 *
 * It answers defect 3 in as many words as it takes, because "4 weeks" on a
 * chip is only unambiguous once a reader has been told that the tile adds
 * those four weeks together. That last clause is earned by the AGGREGATION
 * and nothing else: a chart plots its buckets separately, and an average or a
 * rate is not a total however it is displayed.
 *
 * @param {{ buckets: number, unit: string, through: string } | null} window
 * @param {{ range: string | undefined, days: number | undefined,
 *   aggregation: string | undefined }} opts
 *   `aggregation` is the widget's own field aggregation. Only an additive one
 *   is told it is a total.
 * @returns {string | null}
 */
export function resolvedWindowReason(window, opts = {}) {
  if (!window) return null;
  const { range, days, aggregation } = opts;

  // Daily grain has no bucketing story to tell. Every bucket is a day, so a
  // 30-day range is answered in 30 days and the sentence would explain a
  // discrepancy that does not exist. The chip still names the closing day,
  // which is the one thing daily does hide.
  if (window.unit === "day") return null;

  const plural = `${window.unit}${window.buckets === 1 ? "" : "s"}`;

  // "The range asked for N days" only where the range NAME actually says N
  // days. TIMELINE_DAY_RANGES gives `last_quarter` 92 and `ytd` 365, but
  // those are our internal widths, not the reader's request: nobody asked for
  // 92 days, they asked for a quarter. Putting words in their mouth to set up
  // the contrast would be a small fabrication in a sentence whose whole job
  // is to stop one.
  const named = /^last_(\d+)_days$/.exec(String(range ?? ""));
  const askedDays = named ? Number(named[1]) : null;
  const asked = askedDays && askedDays === days ? `The range asked for ${days} days; ` : "";
  // Only when the tile genuinely adds its buckets, and only above one of them.
  // At a single bucket the figure IS one month, so "the total across all 1 of
  // them, not one month" is both ungrammatical and false; and an average or a
  // rate is not a total at any count. Defect 3 is about a SUM being read as a
  // single period, which is the one case worth warning about.
  const adds = ADDITIVE_AGGREGATIONS.has(String(aggregation));
  const total =
    adds && window.buckets > 1
      ? ` The figure on the face is the total across all ${window.buckets} of them, not one ${window.unit}.`
      : "";
  const opener = asked ? "this" : "This";
  return (
    `${asked}${opener} tile counts whole ${window.unit}s, so it is answered in whole ` +
    `${window.unit}s: ${window.buckets} ${plural}, ending with the last one that ` +
    `finished. Nothing after that ${window.unit} closed is in the number, which is ` +
    `why the window stops short of today.${total}`
  );
}
